import { create } from 'zustand';
import type {
  ExtractionPageMeta,
  PageStatus,
  ApprovalRequest,
  ExtractionPageResult,
} from '../types';

export interface BboxItem {
  id: string;
  type: string;
  bbox: [number, number, number, number]; // normalized [0,1]
  label?: string;
}

interface ExtractionState {
  // Page list (built from triage + separation)
  pages: ExtractionPageMeta[];
  pageIndex: number;
  docTypeFilter: string;

  // PDF state
  currentFile: string | null;

  // Current bboxes on the PDF viewer (synced from ExtractionLeft)
  currentBboxes: BboxItem[];

  // Page review HITL
  pendingReviews: ApprovalRequest[];
  currentReview: ApprovalRequest | null;
  autoApprovePages: boolean;

  // Per-page statuses & live results
  pageStatuses: Record<string, PageStatus>;  // key: "file:page"
  pageResults: Record<string, ExtractionPageResult>;  // key: "file:page"

  // Active zones (zone_id → expanded)
  activeZones: Record<string, boolean>;

  // Per-page user-drawn bboxes (persisted across navigation)
  pageBboxes: Record<string, BboxItem[]>;  // key: "file:page"

  // Edit mode (after extraction gate)
  editMode: boolean;

  // Zoom
  zoom: number;

  // Per-file publish status: key (filename or "filename:page") → publish info
  publishedPlans: Record<string, { published_at: string; location: string; stale?: boolean }>;
  publishingFiles: Record<string, boolean>; // files currently being published

  // Actions
  setPages: (pages: ExtractionPageMeta[]) => void;
  setPageIndex: (idx: number) => void;
  setDocTypeFilter: (filter: string) => void;
  setCurrentFile: (file: string | null) => void;
  setCurrentBboxes: (bboxes: BboxItem[]) => void;
  addPendingReview: (review: ApprovalRequest) => void;
  shiftPendingReview: () => ApprovalRequest | undefined;
  clearPendingReviews: () => void;
  setCurrentReview: (review: ApprovalRequest | null) => void;
  setAutoApprovePages: (auto: boolean) => void;
  setPageStatus: (key: string, status: PageStatus) => void;
  setPageResult: (key: string, result: ExtractionPageResult) => void;
  toggleZone: (zoneId: string) => void;
  setPageBboxes: (key: string, bboxes: BboxItem[]) => void;
  setEditMode: (edit: boolean) => void;
  setZoom: (zoom: number) => void;
  getFilteredPages: () => ExtractionPageMeta[];
  activateReviewForPage: (file: string, page: number) => void;
  restorePageResults: (results: Record<string, ExtractionPageResult>) => void;
  setPublishedPlans: (plans: Record<string, { published_at: string; location: string; stale?: boolean }>) => void;
  setPublishing: (key: string, publishing: boolean) => void;
  markPublished: (key: string, location: string) => void;
  removeFileReviews: (file: string) => void;
}

export const useExtractionStore = create<ExtractionState>((set, get) => ({
  pages: [],
  pageIndex: 0,
  docTypeFilter: '',
  currentFile: null,
  currentBboxes: [],
  pendingReviews: [],
  currentReview: null,
  autoApprovePages: false,
  pageStatuses: {},
  pageResults: {},
  pageBboxes: {},
  activeZones: {},
  editMode: false,
  zoom: 1,
  publishedPlans: {},
  publishingFiles: {},

  setPages: (pages) => set({ pages }),
  setPageIndex: (pageIndex) => set({ pageIndex }),
  setDocTypeFilter: (docTypeFilter) => set({ docTypeFilter }),
  setCurrentFile: (currentFile) => set({ currentFile }),
  setCurrentBboxes: (currentBboxes) => set({ currentBboxes }),

  addPendingReview: (review) =>
    set((s) => {
      // Deduplicate by request_id
      if (s.pendingReviews.some((r) => r.request_id === review.request_id)) return s;
      if (s.currentReview?.request_id === review.request_id) return s;
      return { pendingReviews: [...s.pendingReviews, review] };
    }),

  shiftPendingReview: () => {
    const s = get();
    if (s.pendingReviews.length === 0) return undefined;
    const [first, ...rest] = s.pendingReviews;
    set({ pendingReviews: rest });
    return first;
  },

  clearPendingReviews: () => set({ pendingReviews: [], currentReview: null }),

  setCurrentReview: (currentReview) => set({ currentReview }),
  setAutoApprovePages: (autoApprovePages) => set({ autoApprovePages }),
  setPageStatus: (key, status) =>
    set((s) => ({ pageStatuses: { ...s.pageStatuses, [key]: status } })),
  setPageResult: (key, result) =>
    set((s) => {
      const update: Partial<ExtractionState> = {
        pageResults: { ...s.pageResults, [key]: result },
      };
      // Auto-stale: if this file was published, mark it stale
      const file = key.split(':')[0];
      if (file && s.publishedPlans[file]) {
        update.publishedPlans = {
          ...s.publishedPlans,
          [file]: { ...s.publishedPlans[file], stale: true },
        };
      }
      return update;
    }),
  toggleZone: (zoneId) =>
    set((s) => ({
      activeZones: { ...s.activeZones, [zoneId]: !s.activeZones[zoneId] },
    })),
  setPageBboxes: (key, bboxes) =>
    set((s) => ({ pageBboxes: { ...s.pageBboxes, [key]: bboxes } })),
  setEditMode: (editMode) => set({ editMode }),
  setZoom: (zoom) => set({ zoom }),

  getFilteredPages: () => {
    const s = get();
    if (!s.docTypeFilter) return s.pages;
    return s.pages.filter((p) => p.docType === s.docTypeFilter);
  },

  activateReviewForPage: (file, page) => {
    const s = get();
    // Already reviewing this page
    if (s.currentReview?.file === file && s.currentReview?.page === page) return;
    // Find matching pending review
    const idx = s.pendingReviews.findIndex(r => r.file === file && r.page === page);
    if (idx < 0) return;
    const review = s.pendingReviews[idx];
    const remaining = [...s.pendingReviews];
    remaining.splice(idx, 1);
    // Push current review back to pending if it exists
    const updated: Partial<ExtractionState> = {
      currentReview: review,
      pendingReviews: s.currentReview ? [s.currentReview, ...remaining] : remaining,
    };
    set(updated);
  },

  restorePageResults: (results: Record<string, ExtractionPageResult>) => {
    // Bulk restore without triggering stale detection (used on reload)
    const statuses: Record<string, PageStatus> = {};
    for (const key of Object.keys(results)) {
      statuses[key] = 'done';
    }
    set((s) => ({
      pageStatuses: { ...statuses, ...s.pageStatuses },
      pageResults: { ...results, ...s.pageResults },
    }));
  },
  setPublishedPlans: (plans) => set({ publishedPlans: plans }),
  setPublishing: (key, publishing) =>
    set((s) => ({ publishingFiles: { ...s.publishingFiles, [key]: publishing } })),
  markPublished: (key, location) =>
    set((s) => ({
      publishingFiles: { ...s.publishingFiles, [key]: false },
      publishedPlans: {
        ...s.publishedPlans,
        [key]: { published_at: new Date().toISOString(), location },
      },
    })),
  removeFileReviews: (file) =>
    set((s) => ({
      pendingReviews: s.pendingReviews.filter((r) => r.file !== file),
      currentReview: s.currentReview?.file === file ? null : s.currentReview,
    })),
}));
